import type { SettingValueSemantics } from "./types";

export type QueryValueSemanticKind =
  | NonNullable<SettingValueSemantics["inputKind"]>
  | "options";

const inputKindSearchText: Record<
  NonNullable<SettingValueSemantics["inputKind"]>,
  string
> = {
  color: "color colors colour colours hex brand branding theme appearance",
  email: "email address reply sender recipient",
  textarea: "copy message body long text multiline",
  timezone: "timezone time zone hours schedule availability locale",
  url: "url link website image logo webhook destination",
};

export function createValueSemanticsSearchText(
  valueSemantics: SettingValueSemantics | undefined,
): string | undefined {
  if (!valueSemantics) {
    return undefined;
  }

  const defaultValueText = getDefaultValueSearchText(
    valueSemantics.defaultValue,
  );
  const parts = [
    JSON.stringify(valueSemantics),
    valueSemantics.inputKind,
    valueSemantics.inputKind
      ? inputKindSearchText[valueSemantics.inputKind]
      : undefined,
    valueSemantics.unit,
    defaultValueText,
    valueSemantics.selectionMode === "multiple"
      ? "multiple multi select checklist choices"
      : undefined,
    valueSemantics.options?.length
      ? "option options select dropdown choices"
      : undefined,
    valueSemantics.trueLabel,
    valueSemantics.falseLabel,
    valueSemantics.unsetLabel,
    ...(valueSemantics.options ?? []).flatMap((option) => [
      option.value,
      option.label,
      option.description,
    ]),
  ];

  return parts.filter(isSearchTextPart).join(" ");
}

export function getQueryValueSemanticKinds(
  normalizedQuery: string,
): ReadonlySet<QueryValueSemanticKind> {
  const semanticKinds = new Set<QueryValueSemanticKind>();
  if (
    includesAny(normalizedQuery, [
      "color",
      "colors",
      "colour",
      "colours",
      "red",
      "blue",
      "green",
      "black",
      "white",
      "hex",
      "brand",
      "branding",
      "theme",
    ])
  ) {
    semanticKinds.add("color");
  }
  if (includesAny(normalizedQuery, ["email", "reply address", "sender"])) {
    semanticKinds.add("email");
  }
  if (includesAny(normalizedQuery, ["url", "link", "logo", "image"])) {
    semanticKinds.add("url");
  }
  if (includesAny(normalizedQuery, ["timezone", "time zone"])) {
    semanticKinds.add("timezone");
  }
  if (includesAny(normalizedQuery, ["copy", "message", "text", "body"])) {
    semanticKinds.add("textarea");
  }
  if (
    includesAny(normalizedQuery, [
      "option",
      "options",
      "dropdown",
      "choice",
      "choices",
      "select",
      "placement",
      "position",
    ])
  ) {
    semanticKinds.add("options");
  }

  return semanticKinds;
}

export function adjustFuseScoreForValueSemantics({
  fuseScore,
  querySemanticKinds,
  valueSemantics,
}: {
  fuseScore: number;
  querySemanticKinds: ReadonlySet<QueryValueSemanticKind>;
  valueSemantics: SettingValueSemantics | undefined;
}): number {
  let adjustment = 0;
  if (
    valueSemantics?.inputKind &&
    querySemanticKinds.has(valueSemantics.inputKind)
  ) {
    adjustment += 0.18;
  }

  if (valueSemantics?.options?.length && querySemanticKinds.has("options")) {
    adjustment += 0.08;
  }

  return Math.max(fuseScore - adjustment, 0);
}

function isSearchTextPart(value: string | undefined): value is string {
  return typeof value === "string" && value.length > 0;
}

function getDefaultValueSearchText(
  defaultValue: SettingValueSemantics["defaultValue"] | undefined,
): string | undefined {
  return defaultValue == null ? undefined : String(defaultValue);
}

function includesAny(value: string, needles: readonly string[]): boolean {
  return needles.some((needle) => value.includes(needle));
}
